import { ArrowLeft, Minus, Plus, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import type { WalletEntity } from "@/shared/types/wallet.entity";
import { colocaToast } from "@/shared/utils/toast";

import { balancesFromMovement, useCardWalletAdjustment } from "../../hooks/useCardWalletAdjustment";
import type { AdjustDirection, AdjustInlineProblem, AdjustOutcome } from "../../types/cardWallets.types";
import { unresolvedAdjustAttempts } from "../../utils/adjustAttempt";
import { ADJUST_CURRENCY } from "../../utils/cardWalletAdjust";
import { AdjustBalanceForm, type AdjustFormValues } from "./AdjustBalanceForm";
import { AdjustBalanceResult, type UnconfirmedNote } from "./AdjustBalanceResult";
import { AdjustBalanceReview } from "./AdjustBalanceReview";

const FORM_ID = "card-wallet-adjust-form";

type Step = "form" | "review" | "result";

export interface AdjustBalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  direction: AdjustDirection;
  organizationName: string;
  wallet: WalletEntity;
  /** Available balance as a decimal string. `null` keeps the review out of reach. */
  balance: string | null;
  /** Re-reads the balance; called on entering the review and after a movement is recorded. */
  onRefreshBalance: () => void;
  /** The history should show the movement carrying this reference. */
  onSeeMovement: (reference: string) => void;
  /** The wallet is gone — send the operator back to the organization picker. */
  onResetOrganization: () => void;
  /** A movement was recorded; the host refreshes its list. */
  onRecorded: () => void;
}

/**
 * The adjust, in three steps, with the direction fixed before it opens.
 *
 * `direction` is a PROP and the host keys the dialog on it, so there is no path where it changes under a
 * mounted form — a radio inside the form would let the operator flip it after typing the amount, which is how
 * a credit becomes a debit by accident. The amount itself can never hold a sign.
 */
export function AdjustBalanceDialog({
  open,
  onOpenChange,
  direction,
  organizationName,
  wallet,
  balance,
  onRefreshBalance,
  onSeeMovement,
  onResetOrganization,
  onRecorded,
}: AdjustBalanceDialogProps) {
  const { t } = useTranslations("cardWallets");
  const { submit, resetAttempt, check } = useCardWalletAdjustment();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const restored = unresolvedAdjustAttempts.get(wallet.id);
  const [step, setStep] = useState<Step>(restored ? "result" : "form");
  const [draft, setDraft] = useState<AdjustFormValues>({
    external_reference: restored?.reference ?? "",
    amount: restored?.magnitude ?? "",
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<AdjustOutcome | null>(
    restored ? { kind: "unconfirmed", reference: restored.reference } : null,
  );
  const [inlineProblem, setInlineProblem] = useState<AdjustInlineProblem | null>(null);
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null);
  const [balanceAtReviewEntry, setBalanceAtReviewEntry] = useState<string | null>(null);
  const [unconfirmedNote, setUnconfirmedNote] = useState<UnconfirmedNote>(null);
  const [isChecking, setIsChecking] = useState(false);

  const isCredit = direction === "credit";

  // The throttle countdown. A `setTimeout` rather than an interval so each tick is scheduled by the previous
  // state, which is what makes it stop cleanly at zero.
  useEffect(() => {
    if (retrySeconds === null || retrySeconds <= 0) return;
    const id = setTimeout(() => setRetrySeconds(retrySeconds - 1), 1000);
    return () => clearTimeout(id);
  }, [retrySeconds]);

  const handleFormSubmit = useCallback(
    (values: AdjustFormValues) => {
      setDraft(values);
      setBalanceAtReviewEntry(balance);
      setInlineProblem(null);
      setAcknowledged(false);
      setStep("review");
      // The balance is cached 30 s; the review must state the consequence against the current figure.
      onRefreshBalance();
    },
    [balance, onRefreshBalance],
  );

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    setInlineProblem(null);
    const result = await submit({
      walletId: wallet.id,
      magnitude: draft.amount,
      direction,
      reference: draft.external_reference,
    });
    setSubmitting(false);

    if ("inline" in result) {
      // The throttle wrote nothing and the form is still valid, so the review stays put with a countdown —
      // a dead-end result screen would throw away input that is about to be reused.
      if (result.inline.kind === "rate_limited") {
        setInlineProblem(result.inline);
        setRetrySeconds(result.inline.retryAfterSeconds);
        return;
      }
      // Both of these are checked client-side before the button enables, so reaching them means the server
      // disagrees with us — the amount field is where that gets resolved.
      colocaToast.error(
        result.inline.kind === "insufficient_funds"
          ? t("error_insufficient", {
              balance: `${balance ?? ""} ${ADJUST_CURRENCY}`,
              amount: `${draft.amount} ${ADJUST_CURRENCY}`,
            })
          : t("error_zero"),
      );
      setStep("form");
      return;
    }

    setOutcome(result.settled);
    setStep("result");
    if (result.settled.kind === "done") {
      onRefreshBalance();
      onRecorded();
    }
  }, [submit, wallet.id, draft, direction, balance, onRefreshBalance, onRecorded, t]);

  const handleCheck = useCallback(async () => {
    if (!outcome || outcome.kind !== "unconfirmed") return;
    setIsChecking(true);
    const found = await check(wallet.id, outcome.reference);
    setIsChecking(false);

    if (found.kind === "found") {
      const balances = balancesFromMovement(found.movement);
      setOutcome({
        kind: "done",
        reference: outcome.reference,
        previousBalance: balances.previousBalance ?? "0",
        newBalance: balances.newBalance ?? balance ?? "0",
      });
      setUnconfirmedNote(null);
      onRefreshBalance();
      onRecorded();
      return;
    }
    setUnconfirmedNote(found.kind === "not_placed" ? "not_placed" : "unknown");
  }, [outcome, check, wallet.id, balance, onRefreshBalance, onRecorded]);

  const backToForm = useCallback(() => {
    setStep("form");
    setOutcome(null);
    setUnconfirmedNote(null);
    setInlineProblem(null);
    setRetrySeconds(null);
    resetAttempt();
  }, [resetAttempt]);

  const stepIndex = step === "form" ? 0 : step === "review" ? 1 : 2;
  const confirmDisabled = !acknowledged || submitting || (retrySeconds !== null && retrySeconds > 0);
  const confirmLabel =
    retrySeconds !== null && retrySeconds > 0
      ? t("error_rate_limited_retry", { seconds: retrySeconds })
      : t(isCredit ? "action_confirm_credit" : "action_confirm_debit", {
          amount: `${draft.amount} ${ADJUST_CURRENCY}`,
        });

  const footer = (() => {
    if (step === "form") {
      return (
        <>
          <ColocaButton type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("action_cancel")}
          </ColocaButton>
          <ColocaButton type="submit" form={FORM_ID} variant="primary" disabled={balance === null}>
            {t("action_review")}
          </ColocaButton>
        </>
      );
    }

    if (step === "review") {
      return (
        <>
          <ColocaButton
            type="button"
            variant="ghost"
            icon={ArrowLeft}
            disabled={submitting}
            onClick={() => setStep("form")}
          >
            {t("action_back")}
          </ColocaButton>
          <ColocaButton
            type="button"
            variant={isCredit ? "primary" : "destructive"}
            icon={isCredit ? Plus : Minus}
            loading={submitting}
            disabled={confirmDisabled}
            onClick={() => void handleConfirm()}
          >
            {confirmLabel}
          </ColocaButton>
        </>
      );
    }

    if (outcome?.kind === "done") {
      return (
        <>
          <ColocaButton type="button" variant="ghost" onClick={backToForm}>
            {t("action_another")}
          </ColocaButton>
          <ColocaButton type="button" variant="primary" onClick={() => onOpenChange(false)}>
            {t("action_close")}
          </ColocaButton>
        </>
      );
    }

    if (outcome?.kind === "duplicate_reference") {
      return (
        <>
          <ColocaButton type="button" variant="ghost" onClick={backToForm}>
            {t("error_duplicate_change")}
          </ColocaButton>
          <ColocaButton
            type="button"
            variant="primary"
            icon={Search}
            onClick={() => onSeeMovement(outcome.reference)}
          >
            {t("error_duplicate_see")}
          </ColocaButton>
        </>
      );
    }

    if (outcome?.kind === "unconfirmed") {
      // Once the check says nothing was placed, the same reference is free again — that is the one case
      // where retrying with it is the right move rather than the dangerous one.
      if (unconfirmedNote === "not_placed") {
        return (
          <>
            <ColocaButton type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("action_close")}
            </ColocaButton>
            <ColocaButton type="button" variant="primary" icon={RefreshCw} onClick={backToForm}>
              {t("action_retry")}
            </ColocaButton>
          </>
        );
      }
      return (
        <ColocaButton
          type="button"
          variant="primary"
          icon={Search}
          loading={isChecking}
          onClick={() => void handleCheck()}
        >
          {t("error_unconfirmed_check")}
        </ColocaButton>
      );
    }

    if (outcome?.kind === "wallet_not_found") {
      return (
        <ColocaButton type="button" variant="primary" onClick={onResetOrganization}>
          {t("error_not_found_action")}
        </ColocaButton>
      );
    }

    return (
      <ColocaButton type="button" variant="primary" onClick={() => onOpenChange(false)}>
        {t("action_close")}
      </ColocaButton>
    );
  })();

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      mobileFullscreen
      showCloseButton={!submitting}
      initialFocus={step === "review" ? headingRef : undefined}
      title={t(isCredit ? "dialog_title_credit" : "dialog_title_debit")}
      description={t(isCredit ? "dialog_description_credit" : "dialog_description_debit")}
      toolbar={
        <ColocaStepper
          aria-label={t("stepper_label")}
          currentStep={stepIndex}
          steps={[{ label: t("step_data") }, { label: t("step_review") }, { label: t("step_result") }]}
        />
      }
      footer={footer}
    >
      {step === "form" && (
        <AdjustBalanceForm
          formId={FORM_ID}
          direction={direction}
          organizationName={organizationName}
          balance={balance}
          defaultValues={draft}
          onSubmit={handleFormSubmit}
          onDirty={resetAttempt}
        />
      )}

      {step === "review" && balance !== null && (
        <AdjustBalanceReview
          headingRef={headingRef}
          direction={direction}
          organizationName={organizationName}
          walletId={wallet.id}
          balanceBefore={balance}
          magnitude={draft.amount}
          reference={draft.external_reference}
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
          balanceMoved={balanceAtReviewEntry !== null && balanceAtReviewEntry !== balance}
          inlineProblem={inlineProblem}
          retrySeconds={retrySeconds}
        />
      )}

      {step === "result" && outcome && (
        <AdjustBalanceResult
          outcome={outcome}
          direction={direction}
          organizationName={organizationName}
          walletId={wallet.id}
          magnitude={draft.amount}
          unconfirmedNote={unconfirmedNote}
        />
      )}
    </ColocaDialog>
  );
}
